import { useState } from 'react'
import { useSession } from '@/entities/session'
import type { Task } from '@/entities/task'
import { dateInputToDay, toDateInput } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { toast } from '@/shared/ui/toast'
import { dayFieldHint } from '@/shared/ui/zone-badge'
import { useDeleteTask, useEditTask } from '../api/edit'

interface EditTaskDialogProps {
  task: Task
  projectId: string
  open: boolean
  onClose: () => void
}

export function EditTaskDialog({ task, projectId, open, onClose }: EditTaskDialogProps) {
  const { session } = useSession()
  const isCustomer = session?.kind === 'customer'
  const edit = useEditTask(task.id, projectId)
  const del = useDeleteTask(projectId)
  const [title, setTitle] = useState(task.title)
  const [due, setDue] = useState(toDateInput(task.due))
  const [blocking, setBlocking] = useState(task.blocking ?? false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState('')
  const busy = edit.isPending || del.isPending

  function submit() {
    if (!title.trim()) return setError('Укажите задачу')
    setError('')
    // Срок задачи — календарный день "YYYY-MM-DD": ровно то, что отдаёт
    // <input type="date">. Снятие значения — только через clear_due (null в
    // PATCH не проходит схему).
    const dueDay = dateInputToDay(due)
    edit.mutate(
      {
        title: title.trim(),
        ...(dueDay ? { due: dueDay } : { clear_due: true }),
        // blocking ставит только сотрудник.
        ...(isCustomer ? {} : { blocking }),
      },
      {
        onSuccess: () => {
          toast.success('Сохранено')
          onClose()
        },
        onError: () => toast.error('Не удалось сохранить'),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Задача"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={busy}>
            {edit.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Задача" {...(error ? { error } : {})}>
          {(props) => <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        {/* Срок задачи — день: зоны у него нет, зато есть предпросмотр. */}
        <Field label="Срок" hint={dayFieldHint(due)}>
          {(props) => (
            <Input {...props} type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          )}
        </Field>
        {!isCustomer && (
          <Checkbox
            checked={blocking}
            onChange={(e) => setBlocking(e.target.checked)}
            label="Блокирующая — держит проект на текущем этапе"
            labelClassName="text-sm text-text-secondary"
          />
        )}

        <div className="mt-2 flex flex-col gap-2 border-border-subtle border-t pt-4">
          {confirmDelete ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-text-secondary">Удалить задачу?</span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDelete(false)}
                  disabled={busy}
                >
                  Нет
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    del.mutate(task.id, {
                      onSuccess: () => {
                        toast.success('Задача удалена')
                        onClose()
                      },
                      onError: () => toast.error('Не удалось удалить'),
                    })
                  }
                >
                  Удалить
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="self-start text-accent"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
            >
              Удалить задачу
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  )
}
